/**
 * Lightweight HTML sanitizer to prevent XSS attacks
 * Removes dangerous tags and attributes while preserving safe HTML structure
 */

const DANGEROUS_TAGS = ['script', 'iframe', 'object', 'embed', 'link', 'style', 'meta', 'base', 'form'];
const DANGEROUS_ATTRS = /^on\w+|javascript:|data:/i;

export function sanitizeHtml(html: string): string {
  if (!html) return '';

  const doc = new DOMParser().parseFromString(html, 'text/html');

  // Remove dangerous tags
  DANGEROUS_TAGS.forEach((tag) => {
    const elements = doc.querySelectorAll(tag);
    elements.forEach((el) => el.remove());
  });

  // Remove dangerous attributes
  const allElements = doc.querySelectorAll('*');
  allElements.forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      if (DANGEROUS_ATTRS.test(attr.name) || DANGEROUS_ATTRS.test(attr.value)) {
        el.removeAttribute(attr.name);
      }
    });
  });

  return doc.body.innerHTML;
}

// Strict allow-list sanitizer for Shopware-maintained promo copy (ORT-3110). The
// promo userDescription is editorially maintained rich text: only a handful of
// inline formatting tags and layout wrappers are allowed, each stripped down to
// class/title/alt. Anything else is unwrapped (its text content survives, so a
// stray <script> leaves inert text, never executes) rather than dropped, so
// legitimate copy is never silently lost.
const PROMO_ALLOWED_TAGS = new Set([
  'b', 'strong', 'i', 'em', 'u', 's', 'small', 'sub', 'sup', 'br', 'span', 'div', 'p',
]);
const PROMO_ALLOWED_ATTRS = new Set(['class', 'title', 'alt']);

export function sanitizePromoHtml(html: string): string {
  if (!html) return '';

  const doc = new DOMParser().parseFromString(html, 'text/html');

  // Reverse order so that unwrapping a parent after its children keeps nesting intact.
  const elements = Array.from(doc.body.querySelectorAll('*')).reverse();
  elements.forEach((el) => {
    if (!PROMO_ALLOWED_TAGS.has(el.tagName.toLowerCase())) {
      // Unwrap: replace the disallowed element with its child nodes.
      el.replaceWith(...Array.from(el.childNodes));
      return;
    }
    Array.from(el.attributes).forEach((attr) => {
      if (!PROMO_ALLOWED_ATTRS.has(attr.name.toLowerCase())) {
        el.removeAttribute(attr.name);
      }
    });
  });

  return doc.body.innerHTML;
}
